import React, { useRef, useState } from "react";
import styles from "./styles.module.scss";

/**
 * Segmented IPv4 (+ optional CIDR prefix) input, like a cloud console: four octet boxes each
 * clamped to 0-255 and an optional `/prefix` box clamped to 0-32. Auto-advances on "." or a full
 * octet; backspace on an empty box steps back; pasting a full "a.b.c.d[/p]" into the first box
 * distributes it. Calls onChange with "a.b.c.d" or "a.b.c.d/p" once all four octets are set, else "".
 */
const clampOctet = (s) => {
    const d = s.replace(/\D/g, "").slice(0, 3);
    return d === "" ? "" : String(Math.min(255, parseInt(d, 10)));
};
const clampPrefix = (s) => {
    const d = s.replace(/\D/g, "").slice(0, 2);
    return d === "" ? "" : String(Math.min(32, parseInt(d, 10)));
};
const parseString = (raw) => {
    const [addr, prefix] = String(raw).split("/");
    const parts = addr.split(".");
    return {
        octets: [0, 1, 2, 3].map((i) => clampOctet(parts[i] ?? "")),
        prefix: clampPrefix(prefix ?? ""),
    };
};

const Ipv4Input = ({ onChange, dataTestId }) => {
    const [octets, setOctets] = useState(["", "", "", ""]);
    const [prefix, setPrefix] = useState("");
    const refs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];

    const emit = (o, p) => {
        const complete = o.every((x) => x !== "");
        onChange(complete ? o.join(".") + (p !== "" ? "/" + p : "") : "");
    };

    const changeOctet = (i, raw) => {
        // Pasting a whole address into any box distributes it across the boxes.
        if (/[./]/.test(raw)) {
            const parsed = parseString(raw);
            setOctets(parsed.octets);
            setPrefix(parsed.prefix);
            emit(parsed.octets, parsed.prefix);
            return;
        }
        const v = clampOctet(raw);
        const next = [...octets];
        next[i] = v;
        setOctets(next);
        emit(next, prefix);
        if (v.length === 3 && i < 3) refs[i + 1].current?.focus();
    };

    const changePrefix = (raw) => {
        const v = clampPrefix(raw);
        setPrefix(v);
        emit(octets, v);
    };

    const onKeyDown = (i, e) => {
        if (e.key === "." && i < 4) {
            e.preventDefault();
            refs[i + 1].current?.focus();
        } else if (e.key === "Backspace" && e.target.value === "" && i > 0) {
            refs[i - 1].current?.focus();
        }
    };

    return (
        <span className={styles.ipv4} data-testid={dataTestId}>
            {[0, 1, 2, 3].map((i) => (
                <React.Fragment key={i}>
                    <input
                        ref={refs[i]}
                        className={styles.octet}
                        inputMode="numeric"
                        placeholder="0"
                        data-testid={dataTestId ? `${dataTestId}-o${i}` : undefined}
                        value={octets[i]}
                        onChange={(e) => changeOctet(i, e.target.value)}
                        onKeyDown={(e) => onKeyDown(i, e)}
                    />
                    {i < 3 && <span className={styles.dot}>.</span>}
                </React.Fragment>
            ))}
            <span className={styles.slash}>/</span>
            <input
                ref={refs[4]}
                className={styles.prefix}
                inputMode="numeric"
                placeholder="32"
                data-testid={dataTestId ? `${dataTestId}-prefix` : undefined}
                value={prefix}
                onChange={(e) => changePrefix(e.target.value)}
            />
        </span>
    );
};

export default Ipv4Input;
